import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  startOfMonth,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
} from "date-fns";
import moment from "moment";
import {
  BookingRecurrenceType,
  ResourceBookingSchema,
  ResourceSchema,
  TimeBlock,
  TimeSlot,
} from "../../data/types";
import {
  areDatesInSameMonth,
  areBookingsLessThan12Hours,
  findBookingsForDay,
  dayAvailability,
  calculateAvailableTimeBlocksForSlots,
  isKitchenClosedOnDate,
} from "../../utils/helpers";

type MonthViewProps = {
  currentCalendarDate: Date;
  selectedDay: Date;
  highlightedDays?: Date[];
  resourceBookings: Array<ResourceBookingSchema>;
  userBookings: Array<ResourceBookingSchema>;
  nHours: number;
  handleDateChange: (date: Date) => void;
  handleConfirmBooking?: () => void;
  currentKitchen: ResourceSchema;
  setBlocks: React.Dispatch<React.SetStateAction<TimeBlock[] | undefined>>;
  bookingRecurrence?: BookingRecurrenceType;
  endRecurrence?: string;
  /** When false, no days are highlighted until the user clicks a calendar day. */
  dateSelectionInitiated?: boolean;
};

const ReservationCalendar: React.FC<MonthViewProps> = ({
  userBookings,
  resourceBookings,
  currentCalendarDate,
  nHours,
  currentKitchen,
  setBlocks,
  selectedDay,
  highlightedDays,
  handleDateChange,
  bookingRecurrence = 'once',
  endRecurrence,
  dateSelectionInitiated = false,
}) => {
  const isRecurring =
    bookingRecurrence !== 'once' && Boolean(endRecurrence);

  const displayHighlightedDays = useMemo(() => {
    if (!dateSelectionInitiated) {
      return [];
    }
    if (highlightedDays && highlightedDays.length > 0) {
      return highlightedDays;
    }
    return [];
  }, [dateSelectionInitiated, highlightedDays]);

  const bufferMinutes = currentKitchen?.resource_options?.floor_plan?.buffer_time ?? 0;
  const [bookings, setBookings] = useState<ResourceBookingSchema[]>([]);
  const currentKitchenRef = useRef(currentKitchen);

  const firstDayOfMonth = startOfWeek(startOfMonth(currentCalendarDate));
  const lastDayOfMonth = endOfWeek(endOfMonth(currentCalendarDate));

  const daysInMonth: Date[] = eachDayOfInterval({
    start: firstDayOfMonth,
    end: lastDayOfMonth,
  });

  /* -------------------------------------------------- */
  /*  EFFECTS                                           */
  /* -------------------------------------------------- */

  useEffect(() => {
    const createCurrentBookings = () => {
      const combinedBookings = [...userBookings, ...resourceBookings].filter(
        (booking, index, self) => index === self.findIndex((b) => b.id === booking.id)
      );
      // Only update if the bookings actually changed
      setBookings(prevBookings => {
        const newBookings = combinedBookings as ResourceBookingSchema[];
        if (prevBookings.length !== newBookings.length || 
            !prevBookings.every((booking, index) => booking.id === newBookings[index]?.id)) {
          return newBookings;
        }
        return prevBookings;
      });
    };
    createCurrentBookings();
    // if (currentCalendarDate) {
    //   handleCalendarClick(firstDayOfMonth);
    // }
  }, [userBookings, resourceBookings]); // Run when either booking array changes

  useEffect(() => {
    currentKitchenRef.current = currentKitchen;
  }, [currentKitchen]);

  useEffect(() => {
    if (dateSelectionInitiated && selectedDay && nHours > 0) {
      const dayOfWeek = moment(selectedDay).format("ddd").toLowerCase();
      const dayOptions = currentKitchenRef.current?.resource_options?.[dayOfWeek] as TimeSlot[];
      if (dayOptions && Array.isArray(dayOptions) && dayOptions.length > 0) {
        const availableBlocks = calculateAvailableTimeBlocksForSlots(
          dayOptions,
          nHours,
          findBookingsForDay(moment(selectedDay), bookings || []),
          moment(selectedDay),
          bufferMinutes
        );
        setBlocks(availableBlocks);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSelectionInitiated, nHours, selectedDay, bookings, bufferMinutes]);

  /* -------------------------------------------------- */
  /*  HANDLERS                                          */
  /* -------------------------------------------------- */

  const handleCalendarClick = async (day: Date) => {
    try {
      if (!areDatesInSameMonth(day, currentCalendarDate) || nHours <= 0) {
        return;
      }

      const isToday = moment().startOf('day').isSame(moment(day).startOf('day'));
      const isPastDay = moment().startOf('day').isAfter(moment(day).startOf('day'));

      if (isPastDay) {
        return;
      }

      if (isKitchenClosedOnDate(day, currentKitchen)) {
        return;
      }

      if (!isRecurring && !isToday && !areBookingsLessThan12Hours(day, bookings || [])) {
        return;
      }

      const dayOfWeek = moment(day).format("ddd").toLowerCase();
      const dayOptions = currentKitchen.resource_options?.[dayOfWeek] as TimeSlot[];

      if (!dayOptions || !Array.isArray(dayOptions) || dayOptions.length === 0) {
        return;
      }

      const hasAvailability = dayAvailability(
        day,
        currentKitchen,
        nHours,
        bookings || []
      );

      if (!hasAvailability) {
        return;
      }

      const availableBlocks = calculateAvailableTimeBlocksForSlots(
        dayOptions,
        nHours,
        findBookingsForDay(moment(day), bookings || []),
        moment(day),
        bufferMinutes
      );

      handleDateChange(day);
      if (availableBlocks.length > 0) {
        setBlocks(availableBlocks);
      }
    } catch (error) {
      console.error("Error in handleCalendarClick:", error);
    }
  };

  /* -------------------------------------------------- */
  /*  RENDER                                            */
  /* -------------------------------------------------- */

  return (
    <div className="flex flex-col w-full justify-center place-items-center">
      <div className="grid grid-cols-7 gap-1 gap-y-3 grid-flow-dense w-full">
        {daysInMonth && daysInMonth.length > 0 &&
          daysInMonth.slice(0, 7).map((day) => (
            <div
              key={day.toISOString()}
              className="text-[#4D4D4D] text-[16px] font-raleway p-2 relative col-span-1 text-left md:px-5"
            >
              <div>{format(day, "E")}</div>
            </div>
          ))}

        {daysInMonth && daysInMonth.length > 0 ? (
          daysInMonth.map((day, i) => {
            const isDaySelected = displayHighlightedDays.some((selectedDate) =>
              isSameDay(selectedDate, day)
            );
            const isToday = moment().startOf('day').isSame(moment(day).startOf('day'));
            const isPastDay = moment().startOf('day').isAfter(moment(day).startOf('day'));
            const isClosed = isKitchenClosedOnDate(day, currentKitchen);
            const failsBookingLimit =
              !isToday && !areBookingsLessThan12Hours(day, bookings || []);
            return (
            <div
              key={i}
              data-date={format(day, 'yyyy-MM-dd')}
              {...(isDaySelected ? { 'data-selected': 'true' } : {})}
              className={`font-medium px-[14px] py-[2px] pb-2 relative col-span-1 border-[1px] border-[#E9E9E9] rounded-md h-[45px] w-[65px]  
                ${
                  day &&
                  isDaySelected
                    ? "bg-primaryDark-100 text-white"
                    : day &&
                      (isClosed || !dayAvailability(day, currentKitchen, nHours, bookings || []))
                    ? "!text-gray-300 cursor-not-allowed bg-transparent"
                    : day &&
                      (failsBookingLimit || isPastDay)
                    ? "bg-transparent text-gray-300 cursor-not-allowed"
                    : "hover:bg-primaryDark-400 cursor-pointer hover:text-white"
                }`}
              onClick={(e) => {
                e.stopPropagation();
                handleCalendarClick(day);
              }}
            >
              <div
                className={`${areDatesInSameMonth(day, currentCalendarDate) ? "" : "!text-transparent"} justify-center md:m-2 text-center md:w-1/4 rounded-full md:pt-1`}
              >
                {format(day, "dd")}
              </div>
            </div>
          )})
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default ReservationCalendar;
